import {
  deviceAuthorizationClient,
  jwtClient,
  magicLinkClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { clearQueryCache } from "@/lib/query-client";
import { wipePersistedCache } from "@/lib/query-persister";

const apiURL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001";

// createAuthClient rejects relative URLs like "/api". When NEXT_PUBLIC_API_URL
// is relative (e.g. "/api"), derive the full URL from window.location.origin
// in the browser. During SSR/build, use a placeholder — auth client only runs
// in the browser.
const authBaseURL =
  apiURL.startsWith("http://") || apiURL.startsWith("https://")
    ? apiURL
    : typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost:3001";

export const authClient = createAuthClient({
  baseURL: authBaseURL,
  basePath: "/api/auth",
  // deviceAuthorizationClient — H2-2132, for the /device CLI approval page.
  plugins: [deviceAuthorizationClient(), jwtClient(), magicLinkClient()],
});

/**
 * Get a JWT access token for API calls.
 *
 * Calls Better Auth's /api/auth/token endpoint (JWT plugin) and caches the
 * result in memory until ~30s before the JWT's `exp` claim. The server issues
 * 1h tokens (server/src/lib/auth-provider.ts), so a single fetch typically
 * serves the lifetime of a tab.
 *
 * Why we cache: every SDK middleware calls this on every protected request.
 * Without caching, a session detail page bursts ~150 /api/auth/token requests
 * per minute per tab, which trips CloudFlare's per-IP edge rate-limit (429)
 * and cascades into 401s on every protected API call. See H2-566.
 *
 * Concurrent callers on a cold cache share a single in-flight promise.
 *
 * Failure semantics:
 *   - 401  → cache cleared (signed out), return "".
 *   - 429 / 5xx / network → cache preserved, return cached value or "".
 */
const EXPIRY_SKEW_MS = 30_000;
const TOKEN_URL =
  apiURL.startsWith("http://") || apiURL.startsWith("https://")
    ? `${apiURL}/api/auth/token`
    : "/api/auth/token";

let cachedToken: { value: string; expAt: number } | null = null;
let pendingFetch: Promise<string> | null = null;
// Bumped on clearAccessTokenCache() so a fetch started before sign-out can't
// repopulate the cache for the next user.
let cacheGeneration = 0;

function decodeExpMs(token: string): number | null {
  try {
    const payload = token.split(".")[1] ?? "";
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64)) as { exp?: number };
    if (typeof json.exp === "number") return json.exp * 1000;
  } catch {
    // malformed JWT — fall through to caller
  }
  return null;
}

async function fetchAccessToken(): Promise<string> {
  const generationAtStart = cacheGeneration;
  try {
    const res = await fetch(TOKEN_URL, { credentials: "include" });
    if (!res.ok) {
      if (res.status === 401) {
        // Involuntary session expiry — perform the SAME full scrub as
        // clearAccessTokenCache() so there is no split-brain window where a
        // concurrent fetch in flight could repopulate the cache for the next
        // user.  Bumping cacheGeneration invalidates any in-flight fetch that
        // started before this 401 was received; clearing queryCache purges
        // tenant-scoped React Query data.
        cachedToken = null;
        pendingFetch = null;
        cacheGeneration += 1;
        clearQueryCache();
        // H2-2125: clearQueryCache() only empties memory. The IndexedDB blob
        // survives it, so without this the previous session's tenant-scoped
        // data is restored on the very next load. Fire-and-forget: the caller
        // is a token fetch that must not block on IndexedDB.
        void wipePersistedCache();
      }
      return cachedToken?.value ?? "";
    }
    const data = (await res.json()) as { token?: string };
    if (!data.token) return "";
    const expAt = decodeExpMs(data.token);
    // If the cache was cleared while this fetch was in flight (e.g. sign-out),
    // or the JWT is malformed, return the token but don't cache it — the next
    // call will refetch fresh.
    if (expAt !== null && generationAtStart === cacheGeneration) {
      cachedToken = { value: data.token, expAt };
    }
    return data.token;
  } catch {
    return cachedToken?.value ?? "";
  }
}

export async function getAccessToken(): Promise<string> {
  const now = Date.now();
  if (cachedToken && cachedToken.expAt - EXPIRY_SKEW_MS > now) {
    return cachedToken.value;
  }
  if (pendingFetch) return pendingFetch;
  pendingFetch = fetchAccessToken().finally(() => {
    pendingFetch = null;
  });
  return pendingFetch;
}

/**
 * Clear the in-memory token cache. Call on sign-out so the next session
 * cannot reuse a stale token. Also bumps a generation counter so any fetch
 * already in flight does not repopulate the cache.
 *
 * Also clears the React Query cache (client confidentiality) so switching
 * tenants or signing out never exposes a previous tenant's cached queries.
 */
export function clearAccessTokenCache(): void {
  void resetAllCachedIdentity();
}

/**
 * H2-2125 — the awaitable form, for callers that navigate immediately.
 *
 * `clearAccessTokenCache()` leaves the IndexedDB delete in flight. That is
 * fine when nothing follows it, and wrong when the very next statement is
 * `window.location.reload()`: the reload wins the race and the page comes back
 * holding the cache we just tried to throw away. The org switcher hit exactly
 * this — a system_admin moving between tenants could be handed the previous
 * org's sessions, messages and approval badges after the reload.
 */
export async function clearAccessTokenCacheAndWait(): Promise<void> {
  await resetAllCachedIdentity();
}

/**
 * The scrub itself. Everything here is synchronous except the IndexedDB
 * delete, which is what the two public forms differ on — whether they wait for
 * it. Kept as one body so a future step added to the scrub cannot be added to
 * only one of them.
 */
function resetAllCachedIdentity(): Promise<void> {
  cachedToken = null;
  pendingFetch = null;
  cacheGeneration += 1;
  clearQueryCache();
  return wipePersistedCache();
}
